import {Component, computed, input, output, inject} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {CapabilityFormComponent} from './capability-form/capability-form.component';
import {TableHeader} from '../../../shared/classes/TableHeader';
import {CapabilityDeleteComponent} from './capability-delete/capability-delete.component';
import {CapabilityCreateEmptyComponent} from './capability-create-empty/capability-create-empty.component';
import {Capability} from '../../../classes/models/employee/Capability';
import {DataService} from '../../../services/store/data.service';
import {EmployeePositionId} from '../../../classes/models/employee/Employee';
import {SHARED_IMPORTS} from '../../../shared/standalone-imports';

/**
 * This component handles showing and editing capabilities.
 */
@Component({
  selector: 'app-capabilities',
  templateUrl: './capabilities.component.html',
  styleUrls: ['./capabilities.component.less'],
  imports: [SHARED_IMPORTS]
})
export class CapabilitiesComponent {
  private modalService = inject(NgbModal);
  private dataService = inject(DataService);

  /** Identifiers of an employee and position. */
  readonly employeePositionId = input<EmployeePositionId>(undefined);
  /** A list of all capabilities. */
  readonly capabilities = input<Capability[]>([]);
  /** An event to refresh employee page data. */
  readonly getEmployeePageDataEvent = output();

  /** Headers of the table, where capabilities should be displayed. */
  headers: TableHeader[] = [
    new TableHeader('Capabilities', 'types', false),
    new TableHeader('Start date', 'startDate', false),
    new TableHeader('', '', false)
  ];

  /**
   * Computed signal that returns `true` if the latest capabilities are not empty.
   */
  readonly lastCapabilitiesAreNotEmpty = computed(() => {
    const capabilities = this.capabilities();
    if (capabilities.length === 0) {
      return false;
    }
    const lastCapabilities = capabilities
            .reduce((previous, current) => current.startDate > previous.startDate ? current : previous);
    return lastCapabilities.types.length > 0;
  });

  /**
   * Checks if data is allowed to edit at the given date.
   * @param date the date on which data might be edited.
   */
  canEditAtDate(date: string): boolean {
    return this.dataService.canEditAtDate(date);
  }

  /**
   * Opens a modal window to create a new capability.
   */
  openCreate(): void {
    const modalRef = CapabilityFormComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId(),
      previousCapability: undefined,
      weekExists: false
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  /**
   * Opens a modal window to update a capability.
   */
  openUpdate(capability: Capability): void {
    const modalRef = CapabilityFormComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId(),
      previousCapability: capability,
      weekExists: true
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  /**
   * Opens a modal window to delete capabilities by actually creating new capabilities having empty capability types.
   */
  openCreateEmpty(): void {
    const modalRef = CapabilityCreateEmptyComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId()
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  /**
   * Open a modal window to delete a specific capability.
   * @param capability a capability to delete.
   */
  openDelete(capability: Capability): void {
    const modalRef = CapabilityDeleteComponent.open(this.modalService, {
      capability: capability,
      employeePositionId: this.employeePositionId()
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }
}